import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Trash2, Loader2, Image as ImageIcon, FileText, Download,
  LayoutGrid, List as ListIcon, ZoomIn,
} from "lucide-react";
import { toast } from "sonner";

const POSITIONS = [
  { value: "", label: "— seçilmedi —" },
  { value: "on", label: "Ön" },
  { value: "arka", label: "Arka" },
  { value: "sol_kol", label: "Sol Kol" },
  { value: "sag_kol", label: "Sağ Kol" },
  { value: "gogus", label: "Göğüs" },
  { value: "yaka", label: "Yaka" },
];

/**
 * ArtworkLibrary — drag-and-drop file uploader + gallery for a customer's
 * logo/artwork assets. Reads/writes /api/artwork/*.
 */
export default function ArtworkLibrary({ customerId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [label, setLabel] = useState("");
  const [position, setPosition] = useState("");
  const [blobUrls, setBlobUrls] = useState({}); // id -> blob url
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [hoverId, setHoverId] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/artwork/customer/${customerId}`);
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (customerId) load(); /* eslint-disable-next-line */ }, [customerId]);

  // Fetch preview blobs for images (auth cookies handle it)
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      for (const it of items) {
        if (blobUrls[it.id]) continue;
        if (!it.content_type?.startsWith("image/")) continue;
        try {
          const resp = await api.get(`/artwork/download/${it.id}`, {
            responseType: "blob",
            signal: controller.signal,
          });
          const url = URL.createObjectURL(resp.data);
          setBlobUrls((prev) => ({ ...prev, [it.id]: url }));
        } catch {
          // ignore preview failure
        }
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line
  }, [items]);

  // Cleanup blob URLs on unmount
  useEffect(() => () => {
    Object.values(blobUrls).forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line
  }, []);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("customer_id", customerId);
        fd.append("label", label || "");
        fd.append("position", position || "");
        fd.append("file", file);
        try {
          await api.post("/artwork/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`${file.name} yüklendi`);
        } catch (err) {
          toast.error(`${file.name}: ${formatApiError(err)}`);
        }
      }
      setLabel(""); setPosition("");
      await load();
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remove = async (item) => {
    if (!window.confirm(`"${item.original_filename}" silinsin mi?`)) return;
    try {
      await api.delete(`/artwork/${item.id}`);
      toast.success("Silindi");
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const download = async (item) => {
    try {
      const resp = await api.get(`/artwork/download/${item.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = item.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-5">
      {/* Uploader */}
      <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Etiket (opsiyonel)</Label>
            <Input
              data-testid="artwork-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Örn: Ana logo v2"
              className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase text-slate-500 font-mono">Konum (opsiyonel)</Label>
            <Select value={position || "none"} onValueChange={(v) => setPosition(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="artwork-position" className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {POSITIONS.map((p) => (
                  <SelectItem key={p.value || "none"} value={p.value || "none"}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div
          data-testid="artwork-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-blue-500 bg-blue-950/20"
              : "border-slate-700 hover:border-slate-600 bg-slate-950"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,application/pdf"
            className="hidden"
            data-testid="artwork-file-input"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-blue-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
            </div>
          ) : (
            <>
              <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-blue-500" : "text-slate-500"}`} />
              <div className="text-sm text-slate-200 font-semibold">
                Dosyaları sürükleyin veya tıklayın
              </div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                PNG · JPG · SVG · PDF · max 15 MB
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Gallery */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 rounded-sm p-10 text-center">
          <ImageIcon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="text-sm text-slate-400">Bu müşteri için henüz logo/artwork yok.</div>
        </Card>
      ) : (
        <>
          {/* View toggle */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest font-mono text-slate-500">
              {items.length} logo
            </div>
            <div className="inline-flex rounded-sm border border-slate-800 bg-slate-900 p-0.5">
              <button
                data-testid="artwork-view-grid"
                onClick={() => setViewMode("grid")}
                className={`px-2.5 py-1 rounded-sm text-xs flex items-center gap-1 ${
                  viewMode === "grid" ? "bg-slate-800 text-blue-400" : "text-slate-400"
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Izgara
              </button>
              <button
                data-testid="artwork-view-list"
                onClick={() => setViewMode("list")}
                className={`px-2.5 py-1 rounded-sm text-xs flex items-center gap-1 ${
                  viewMode === "list" ? "bg-slate-800 text-blue-400" : "text-slate-400"
                }`}
              >
                <ListIcon className="w-3.5 h-3.5" /> Liste
              </button>
            </div>
          </div>

          {viewMode === "grid" ? (
            <div
              className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2.5"
              data-testid="artwork-grid"
            >
              {items.map((it) => (
                <Card
                  key={it.id}
                  className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden group relative"
                  data-testid={`artwork-card-${it.id}`}
                  onMouseEnter={() => setHoverId(it.id)}
                  onMouseLeave={() => setHoverId((cur) => (cur === it.id ? null : cur))}
                >
                  <div className="aspect-square bg-slate-950 flex items-center justify-center relative">
                    {it.content_type?.startsWith("image/") && blobUrls[it.id] ? (
                      <img
                        src={blobUrls[it.id]}
                        alt={it.original_filename}
                        className="max-w-full max-h-full object-contain p-1.5"
                      />
                    ) : it.content_type === "application/pdf" ? (
                      <FileText className="w-8 h-8 text-red-400" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-slate-700" />
                    )}
                    {it.position && (
                      <span className="absolute top-1 left-1 text-[8px] uppercase font-mono bg-slate-950/90 border border-slate-700 text-blue-300 px-1 py-px rounded-sm">
                        {POSITIONS.find((p) => p.value === it.position)?.label || it.position}
                      </span>
                    )}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/50 transition-opacity flex items-center justify-center pointer-events-none">
                      <ZoomIn className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="p-1.5 space-y-0.5">
                    <div className="text-[11px] font-semibold text-slate-100 truncate" title={it.original_filename}>
                      {it.label || it.original_filename}
                    </div>
                    <div className="flex items-center gap-1 pt-0.5">
                      <button
                        data-testid={`artwork-download-${it.id}`}
                        onClick={() => download(it)}
                        className="text-[10px] text-blue-400 hover:text-blue-300 flex-1 flex items-center justify-center gap-0.5 py-0.5"
                        title="İndir"
                      >
                        <Download className="w-3 h-3" /> İndir
                      </button>
                      <button
                        data-testid={`artwork-delete-${it.id}`}
                        onClick={() => remove(it)}
                        className="text-slate-500 hover:text-red-400 p-0.5"
                        title="Sil"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Hover enlarge overlay */}
                  {hoverId === it.id && blobUrls[it.id] && it.content_type?.startsWith("image/") && (
                    <div
                      className="hidden md:block absolute z-40 pointer-events-none"
                      style={{
                        top: "-8px",
                        left: "calc(100% + 8px)",
                        width: "260px",
                        height: "260px",
                      }}
                    >
                      <div className="w-full h-full bg-slate-950 border-2 border-blue-500 rounded-sm shadow-2xl flex items-center justify-center overflow-hidden">
                        <img
                          src={blobUrls[it.id]}
                          alt=""
                          className="max-w-full max-h-full object-contain p-2"
                        />
                      </div>
                      <div className="mt-1 text-[11px] text-blue-300 font-mono bg-slate-950/90 border border-slate-800 px-2 py-1 rounded-sm truncate">
                        {it.label || it.original_filename} · {(it.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <div className="border border-slate-800 rounded-sm overflow-hidden" data-testid="artwork-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-950 border-b border-slate-800">
                  <tr className="text-[10px] uppercase font-mono text-slate-500">
                    <th className="text-left px-3 py-2 w-16">Önizleme</th>
                    <th className="text-left px-3 py-2">Ad</th>
                    <th className="text-left px-3 py-2">Konum</th>
                    <th className="text-right px-3 py-2">Boyut</th>
                    <th className="text-right px-3 py-2">Tarih</th>
                    <th className="px-3 py-2 w-24">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {items.map((it) => (
                    <tr key={it.id} className="hover:bg-slate-900/40" data-testid={`artwork-row-${it.id}`}>
                      <td className="px-3 py-2">
                        <div
                          className="w-10 h-10 bg-slate-950 rounded-sm flex items-center justify-center overflow-hidden relative group"
                          onMouseEnter={() => setHoverId(it.id)}
                          onMouseLeave={() => setHoverId((cur) => (cur === it.id ? null : cur))}
                        >
                          {it.content_type?.startsWith("image/") && blobUrls[it.id] ? (
                            <img src={blobUrls[it.id]} alt="" className="max-w-full max-h-full object-contain" />
                          ) : it.content_type === "application/pdf" ? (
                            <FileText className="w-4 h-4 text-red-400" />
                          ) : (
                            <ImageIcon className="w-4 h-4 text-slate-700" />
                          )}
                          {hoverId === it.id && blobUrls[it.id] && it.content_type?.startsWith("image/") && (
                            <div className="hidden md:block absolute z-40 pointer-events-none top-0 left-12 w-56 h-56 bg-slate-950 border-2 border-blue-500 rounded-sm shadow-2xl flex items-center justify-center overflow-hidden">
                              <img src={blobUrls[it.id]} alt="" className="max-w-full max-h-full object-contain p-2" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-100 truncate max-w-xs" title={it.original_filename}>
                        {it.label || it.original_filename}
                      </td>
                      <td className="px-3 py-2 text-blue-300 text-xs">
                        {POSITIONS.find((p) => p.value === it.position)?.label || it.position || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-slate-400">
                        {(it.size / 1024).toFixed(1)} KB
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">
                        {new Date(it.created_at).toLocaleDateString("tr-TR")}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            data-testid={`artwork-list-download-${it.id}`}
                            onClick={() => download(it)}
                            className="text-blue-400 hover:text-blue-300 p-1"
                            title="İndir"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`artwork-list-delete-${it.id}`}
                            onClick={() => remove(it)}
                            className="text-slate-500 hover:text-red-400 p-1"
                            title="Sil"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
